import { BookOpen, Database, type LucideIcon, Puzzle, Search, Settings, Shield } from 'lucide-react'
import { matchPath } from 'react-router-dom'
import type { GlobalRole } from '@/services/api/auth'

export type SectionKey = 'cases' | 'data' | 'extensions' | 'admin' | 'docs' | 'settings'

export interface Section {
  key: SectionKey
  label: string
  icon: LucideIcon
  /** Routes that belong to this section (used to detect the active section from the URL). */
  matchPatterns: string[]
  /** Where to land when the user clicks this rail item from a different section. */
  defaultRoute: string
  /** Minimum global role required to see this section (omit = everyone). */
  minRole?: GlobalRole
  /** When true, the section name is not added to the breadcrumb (only the leaf page is shown). */
  omitFromBreadcrumb?: boolean
  /** When true, the section is not shown in the icon rail (routes stay reachable by URL). */
  hideFromRail?: boolean
}

/** The app's landing page — where the brand mark leads from anywhere in the shell. */
export const HOME_ROUTE = '/cases/home'

export const SECTIONS: Section[] = [
  {
    key: 'cases',
    label: 'Cases',
    icon: Search,
    matchPatterns: ['/cases/*', '/cases'],
    defaultRoute: HOME_ROUTE,
  },
  {
    key: 'data',
    label: 'Data',
    icon: Database,
    matchPatterns: ['/data/*', '/data'],
    defaultRoute: '/data',
  },
  {
    key: 'extensions',
    label: 'Extensions',
    icon: Puzzle,
    matchPatterns: ['/extensions/*', '/extensions'],
    defaultRoute: '/extensions',
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: Shield,
    matchPatterns: ['/admin/*'],
    defaultRoute: '/admin/labels',
    minRole: 'admin',
    omitFromBreadcrumb: true,
  },
  {
    key: 'docs',
    label: 'Docs',
    icon: BookOpen,
    matchPatterns: ['/docs/*', '/docs'],
    defaultRoute: '/docs',
    // Superseded by the standalone docs-site served at /docs; the in-app
    // pages stay reachable by URL for reference but are not linked.
    hideFromRail: true,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    matchPatterns: ['/settings/*', '/settings'],
    defaultRoute: '/settings',
  },
]

/**
 * Whether a section has sub-navigation of its own (the SectionSidebar on
 * desktop, a nested list in the phone drawer). Data is a flat top-level
 * product, cases own their nav through the case-mode rail, and /cases/home is
 * a standalone landing page — none of those get one.
 */
export function sectionHasSidebar(s: Section | null | undefined): s is Section {
  return s != null && s.key !== 'data' && s.key !== 'cases' && s.key !== 'extensions'
}

export function getSectionForPath(pathname: string): Section | undefined {
  for (const s of SECTIONS) {
    for (const pattern of s.matchPatterns) {
      if (matchPath(pattern, pathname)) return s
    }
  }
  return undefined
}
